<template>
  <v-card>
    <v-form ref="editLineForm" @submit.prevent="submitEditLine()">
      <v-container style="padding-bottom: 0px">
        <v-layout row wrap>
          <dialog-header :title="'Edit'" :details="!batchEntry ? `Line ${line.id}` : null"></dialog-header>
          <v-flex xs12>
            <div class="flex-container" bottom v-if="!!complianceIssues.length">
              <span class="red--text">
                <div v-for="(file,i) in masterFiles" :key="i">
                  <span
                    class="vertical-center-container"
                    v-if="!!complianceIssues.filter(item => item.masterFile == file.value).length"
                  >
                    <v-icon
                      class="pr-2"
                      :color="$store.state.global.pages[file.value]['color']"
                    >{{$store.state.global.pages[file.value]['icon']}}</v-icon>
                    {{ `${file.name}` }}
                    <span
                      v-for="(item,i) in complianceIssues.filter(item => item.masterFile == file.value)"
                      :key="i"
                      class="ml-1"
                    >{{ `${item.type}` }}: {{ makeShortDate(item.date) }}.</span>
                  </span>
                </div>
              </span>
            </div>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Tip"
              color="primary"
              :items="filteredTips"
              @change="editedLine.spoilId"
              v-model="editedLine.tipId"
              item-text="name"
              item-value="id"
              :loading="loadingTips"
              :rules="tipRules"
              :prepend-icon="$store.state.global.pages.tip.icon"
              clearable
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Spoil"
              color="primary"
              :items="filteredSpoils"
              v-model="editedLine.spoilId"
              item-text="name"
              item-value="id"
              :loading="loadingSpoils"
              :rules="spoilRules"
              :prepend-icon="$store.state.global.pages.spoil.icon"
              clearable
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="Sub-Contractor"
              color="primary"
              :items="contractors"
              v-model="contractorId"
              item-text="name"
              item-value="id"
              :loading="loadingContractors"
              :prepend-icon="$store.state.global.pages.contractor.icon"
              clearable
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Driver"
              color="primary"
              :items="filteredDrivers"
              v-model="editedLine.driverId"
              item-text="name"
              item-value="id"
              :loading="loadingTips"
              :rules="driverRules"
              :prepend-icon="$store.state.global.pages.driver.icon"
              clearable
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <v-autocomplete
              label="* Vehicle"
              color="primary"
              :items="filteredVehicles"
              v-model="editedLine.vehicleId"
              item-text="vehicleName_vehicleType"
              item-value="id"
              :loading="loadingVehicles"
              :rules="vehicleRules"
              :prepend-icon="$store.state.global.pages.vehicle.icon"
              clearable
            ></v-autocomplete>
          </v-flex>
          <v-flex xs12 sm6>
            <time-picker-wrapper
              :value="startTime"
              v-model="startTime"
              label="* Start Time"
              color="primary"
            ></time-picker-wrapper>
          </v-flex>
          <v-flex xs12 sm6>
            <v-text-field
              label="Notes"
              color="primary"
              v-model="editedLine.notes"
              prepend-icon="note"
              clearable
            ></v-text-field>
          </v-flex>
        </v-layout>
      </v-container>
      <v-card-actions>
        <v-tooltip top>
          <div
            bottom
            class="vertical-center-container flex-container-end"
            slot="activator"
            v-if="isDuplicate"
          >
            <v-icon color="yellow" class="mr-2">warning</v-icon>
            <span>Duplicate</span>
            <span class="ml-1" v-if="duplicateDriver">Driver</span>
            <span class="ml-1" v-if="duplicateDriver && duplicateVehicle">&</span>
            <span class="ml-1" v-if="duplicateVehicle">Vehicle</span>
          </div>
          <span
            v-if="duplicateDriver"
          >Driver (Batch {{ duplicateDriver.allocationBatchHeaderId }} Line {{ duplicateDriver.id }})</span>
          <span
            v-if="duplicateVehicle"
            class="ml-1"
          >Vehicle (Batch {{ duplicateVehicle.allocationBatchHeaderId }} Line {{ duplicateVehicle.id }})</span>
        </v-tooltip>

        <v-spacer></v-spacer>
        <div v-if="different">
          <v-btn :color="primaryGrey" flat @click="discard()">Discard?</v-btn>
          <v-btn
            v-if="complianceIssues.length < 1"
            :color="primaryGreen"
            flat
            type="submit"
            :loading="loading"
          >Save</v-btn>
        </div>
        <div v-else>
          <v-btn :color="primaryGrey" flat @click="close()">Close</v-btn>
        </div>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import Store from '@/store/computed/storeHelpers'
import AllocationService from '@/services/requests/allocationService'

// MIXINS
import getContractors from '@/services/mixins/getMasterFiles/getContractors'
import getDrivers from '@/services/mixins/getMasterFiles/getDrivers'
import getVehicles from '@/services/mixins/getMasterFiles/getVehicles'
import getTips from '@/services/mixins/getMasterFiles/getTips'
import getSpoils from '@/services/mixins/getMasterFiles/getSpoils'
import LineValidation from '../../validation/prelimAllocationValidation'
import FormatDates from '@/services/mixins/formatDates'
import FilterDriverVehicles from '@/services/mixins/filterDriverVehicles'
import FilterTipSpoils from '@/services/mixins/filterTipSpoils'
import obj from '../../mixins/obj'

// COMPONENTS
import DialogHeader from '@/components/ui/dialogHeader'
import TimePickerWrapper from '@/components/ui/timePickerWrapper'

export default {
  mixins: [
    getContractors,
    getDrivers,
    getVehicles,
    getTips,
    getSpoils,
    LineValidation,
    FormatDates,
    FilterDriverVehicles,
    FilterTipSpoils,
    obj,
  ],
  components: {
    DialogHeader,
    TimePickerWrapper,
  },
  props: {
    line: Object,
    batch: Object,
    batchEntry: Boolean,
  },
  data() {
    return {
      editedLine: Object.assign({}, this.line),
      prefix: 'editedLine',
      loading: false,
      startTime: '',
      originalStartTime: '',
      contractorId: null,
    }
  },
  mounted() {
    this.initTime()
    this.contractorId = this.editedLine.driver.contractorId
  },
  watch: {
    line: {
      deep: true,
      handler(val) {
        this.discard()
        this.initTime()
      },
    },
  },
  computed: {
    batchLinesByDate: Store.getSet({
      store: 'batchAllocations',
      prop: 'batchLinesByDate',
    }),
    fetchBatches: Store.getSet({
      store: 'allocationStaging',
      prop: 'fetchBatches',
    }),
    different() {
      if (!this._.isEqual(this.line, this.editedLine)) return true
      if (this.originalStartTime != this.startTime) return true
      return false
    },
    driverId() {
      return this.editedLine.driverId
    },
    vehicleId() {
      return this.editedLine.vehicleId
    },
    projectId() {
      return this.batch.site.project.id
    },
    tipId() {
      return this.editedLine.tipId
    },
    spoilId() {
      return this.editedLine.spoilId
    },
    duplicateDriver() {
      const duplicate = this.batchLinesByDate.find(
        (line) =>
          this.editedLine.driverId === line.driverId &&
          !line.duplicate &&
          this.editedLine.id !== line.id
      )
      return duplicate
    },
    duplicateVehicle() {
      const duplicate = this.batchLinesByDate.find(
        (line) =>
          this.editedLine.vehicleId === line.vehicleId &&
          !line.duplicate &&
          this.editedLine.id !== line.id
      )
      return duplicate
    },
    isDuplicate() {
      return !!this.duplicateDriver || !!this.duplicateVehicle
    },
    complianceIssues() {
      var expiryDates = []
      var expired = []
      if (!!this.contractorObj) {
        expiryDates.push({
          masterFile: 'contractor',
          date: this.contractorObj.compensationInsuranceExpiry,
          type: 'Compensation Insurance',
        })
        expiryDates.push({
          masterFile: 'contractor',
          date: this.contractorObj.liabilityInsuranceExpiry,
          type: 'Liability Insurance',
        })
        expiryDates.push({
          masterFile: 'contractor',
          date: this.contractorObj.plantAndEquipInsuranceExpiry,
          type: 'Plant/Equipment Insurance',
        })
      }
      if (!!this.driverObj) {
        expiryDates.push({
          masterFile: 'driver',
          date: this.driverObj.licenceExpiry,
          type: 'Licence',
        })
        expiryDates.push({
          masterFile: 'driver',
          date: this.driverObj.vocExpiry,
          type: 'VOC',
        })
        expiryDates.push({
          masterFile: 'driver',
          date: this.driverObj.soaExpiry,
          type: 'Statement of Attainment',
        })
      }
      if (!!this.vehicleObj) {
        expiryDates.push({
          masterFile: 'vehicle',
          date: this.vehicleObj.registrationExpiry,
          type: 'Registration',
        })
        expiryDates.push({
          masterFile: 'vehicle',
          date: this.vehicleObj.pinkSlipExpiry,
          type: 'Pink Slip',
        })
      }
      if (!expiryDates.length) return expired

      for (var expiryDate of expiryDates) {
        if (!!expiryDate.date) {
          var expiryDateObj = new Date(expiryDate.date)
          var today = new Date()
          var daysToExpired = Math.ceil(
            (expiryDateObj - today) / 1000 / 60 / 60 / 24
          )
          if (daysToExpired < 1) expired.push(expiryDate)
        }
      }
      return expired
    },
  },
  methods: {
    close() {
      this.$emit('close')
    },
    discard() {
      this.editedLine = Object.assign({}, this.line)
      this.close()
    },
    submitEditLine() {
      if (!this.$refs.editLineForm.validate()) {
        this.snack({ text: 'Please correct form errors', color: 'red' })
        return
      }
      if (!this.batchEntry) {
        this.save()
      } else {
        this.batchEntrySave()
      }
    },
    batchEntrySave() {
      const duplicateLines = []
      if (this.duplicateDriver)
        duplicateLines.push({
          batchId: this.duplicateDriver.allocationBatchHeaderId,
          lineId: this.duplicateDriver.id,
          type: 'Driver',
        })
      if (this.duplicateVehicle)
        duplicateLines.push({
          batchId: this.duplicateVehicle.allocationBatchHeaderId,
          lineId: this.duplicateVehicle.id,
          type: 'Vehicle',
        })
      this.editedLine.startTime = this.startTime
      this.editedLine.driver = this.driverObj
      this.editedLine.vehicle = this.vehicleObj
      this.editedLine.contractor = this.contractorObj
      this.editedLine.tip = this.tipObj
      this.editedLine.spoil = this.spoilObj
      this.editedLine.duplicate = this.isDuplicate
      this.editedLine.duplicateLines = duplicateLines
      this.$store.commit('batchAllocations/updateArrayStateByValue', {
        prop: 'allocations',
        value: this.line,
        editedValue: this.editedLine,
      })
      this.snack({ text: 'Line was edited!', color: 'green' })
    },
    save() {
      this.loading = true
      this.editedLine.startTime = this.startTime

      AllocationService.updateBatchLine(this.editedLine)
        .then((result) => {
          this.snack({ text: 'Line was edited!', color: 'green' })
          this.close()
          this.fetchBatches = true
        })
        .catch((err) => {
          this.loading = false
          this.snack({ text: err.response.data.message, color: 'red' })
        })
    },
    initTime() {
      var time = this.editedLine.startTime
      if (!this.batchEntry) time = this.getUTCTime(this.editedLine.startTime)
      this.originalStartTime = time
      this.startTime = time
    },
  },
}
</script>